// Field Sales v2 — Route Planner + Follow-up Tasks + Performance Dashboard
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, asList } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { format, isPast, differenceInDays } from "date-fns";
import {
  Route as RouteIcon, MapPin, Loader2, Plus, ArrowLeft, Check, X, Trash2,
  ListTodo, Calendar, AlertTriangle, Star, Clock, Flame, User as UserIcon,
  BarChart3, TrendingUp, Users, IndianRupee, Phone,
} from "lucide-react";

function Header({ eyebrow, title, actions }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        {eyebrow && <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A5A50]">{eyebrow}</div>}
        <h1 className="mt-1 font-serif text-3xl md:text-4xl text-[#0F1E15]">{title}</h1>
      </div>
      {actions}
    </div>
  );
}

const PRIORITY_ORDER = { high: 0, normal: 1, low: 2 };

// ============================================================
// ROUTE PLANNER
// ============================================================
export function RoutePlannerPage() {
  const { user } = useAuth();
  const canTeam = ["admin", "super-admin", "manager", "accounts"].includes(user?.role);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [filterSp, setFilterSp] = useState("");
  const [team, setTeam] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterSp ? { salesperson_id: filterSp } : {};
      const { data } = await api.get("/field-sales/route-plans", { params });
      setPlans(asList(data));
    } catch { toast.error("Failed to load routes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSp]);
  useEffect(() => { if (canTeam) { (async () => {
    try { const { data } = await api.get("/field-sales/team/salespeople"); setTeam(asList(data)); } catch (err) { void err; }
  })(); } }, [canTeam]);

  return (
    <div className="space-y-6" data-testid="route-planner-page">
      <Header eyebrow="Field Sales" title="Route planner" actions={
        <Button className="rounded-full h-11 bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenNew(true)} data-testid="new-route-btn"><Plus size={14} className="mr-1"/> New route</Button>
      }/>
      {canTeam && (
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Salesperson</Label>
          <Select value={filterSp || "all"} onValueChange={(v) => setFilterSp(v === "all" ? "" : v)}>
            <SelectTrigger className="w-64"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All team</SelectItem>{team.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {loading ? <div className="text-sm text-[#4A5A50]">Loading…</div> : plans.length === 0 ? (
        <Card className="border-dashed border-[#E4DFCF] rounded-xl"><CardContent className="p-8 text-center text-sm text-[#4A5A50]">
          <RouteIcon className="mx-auto mb-2 text-[#0F5E39]" size={32}/>
          No routes yet. Plan a day of stops for your salesperson.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {plans.map(p => <RouteCard key={p.id} plan={p}/>)}
        </div>
      )}
      <NewRouteDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} canTeam={canTeam} team={team} defaultSp={user?.id}/>
    </div>
  );
}

function RouteCard({ plan }) {
  const nav = useNavigate();
  const total = plan.stops?.length || 0;
  const done = (plan.stops || []).filter(s => s.status === "visited").length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Card className="border-[#E4DFCF] rounded-xl hover:shadow-md transition cursor-pointer" onClick={() => nav(`/field/routes/${plan.id}`)} data-testid={`route-card-${plan.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{plan.plan_date ? format(new Date(plan.plan_date), "dd MMM yyyy · EEE") : "—"}</div>
            <div className="mt-1 font-serif text-lg">{plan.title || `Route · ${plan.salesperson_name || ""}`}</div>
            <div className="mt-2 text-xs text-[#4A5A50] inline-flex items-center gap-1"><UserIcon size={12}/> {plan.salesperson_name || "—"}</div>
          </div>
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#F1EDDF] text-[#4A5A50]">{done}/{total}</span>
        </div>
        <div className="mt-4 h-1 bg-[#F1EDDF] rounded-full overflow-hidden">
          <div className="h-full bg-[#0F5E39] transition-all" style={{ width: `${pct}%` }}/>
        </div>
        {plan.total_km_estimate != null && <div className="mt-2 text-xs text-[#4A5A50]">≈ {plan.total_km_estimate} km</div>}
      </CardContent>
    </Card>
  );
}

function NewRouteDialog({ open, onClose, onSaved, canTeam, team, defaultSp }) {
  const [f, setF] = useState({ salesperson_id: defaultSp || "", plan_date: format(new Date(), "yyyy-MM-dd"), title: "", total_km_estimate: "" });
  const [stops, setStops] = useState([]);   // [{contact_id, contact_name}]
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setF({ salesperson_id: defaultSp || "", plan_date: format(new Date(), "yyyy-MM-dd"), title: "", total_km_estimate: "" }); setStops([]); setContactSearch(""); setContactResults([]); } }, [open, defaultSp]);

  useEffect(() => {
    if (!contactSearch || contactSearch.length < 2) { setContactResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/contacts", { params: { search: contactSearch, limit: 15 } }); if (!cancelled) setContactResults(asList(data)); } catch (err) { void err; }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [contactSearch]);

  const addStop = (c) => {
    if (stops.find(s => s.contact_id === c.id)) return;
    setStops([...stops, { contact_id: c.id, contact_name: c.name, sequence: stops.length + 1, phone: c.phone, address: c.address || c.city }]);
    setContactSearch(""); setContactResults([]);
  };
  const removeStop = (idx) => setStops(stops.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= stops.length) return;
    const arr = [...stops]; [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setStops(arr.map((s, i) => ({ ...s, sequence: i + 1 })));
  };

  const submit = async () => {
    if (!f.salesperson_id) { toast.error("Pick salesperson"); return; }
    if (!f.plan_date) { toast.error("Pick date"); return; }
    setSaving(true);
    try {
      await api.post("/field-sales/route-plans", {
        salesperson_id: f.salesperson_id,
        plan_date: new Date(f.plan_date + "T00:00:00Z").toISOString(),
        title: f.title || null,
        stops,
        total_km_estimate: f.total_km_estimate === "" ? null : Number(f.total_km_estimate),
      });
      toast.success("Route created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-route-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Plan a new route</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          {canTeam && (
            <div className="col-span-2"><Label>Salesperson</Label>
              <Select value={f.salesperson_id || undefined} onValueChange={(v) => setF({ ...f, salesperson_id: v })}>
                <SelectTrigger data-testid="route-salesperson"><SelectValue placeholder="Pick"/></SelectTrigger>
                <SelectContent>{team.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Date</Label><Input type="date" value={f.plan_date} onChange={(e) => setF({ ...f, plan_date: e.target.value })} data-testid="route-date"/></div>
          <div><Label>Est. km</Label><Input type="number" value={f.total_km_estimate} onChange={(e) => setF({ ...f, total_km_estimate: e.target.value })}/></div>
          <div className="col-span-2"><Label>Route name</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="North Delhi · Karol Bagh" data-testid="route-title"/></div>
          <div className="col-span-2">
            <Label>Add stops (search contacts)</Label>
            <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Type customer name / phone…" data-testid="route-contact-search"/>
            {contactResults.length > 0 && (
              <div className="mt-1 border border-[#E4DFCF] rounded-lg max-h-40 overflow-y-auto">
                {contactResults.map(c => (
                  <button key={c.id} type="button" onClick={() => addStop(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F1EDDF] flex justify-between">
                    <span>{c.name}</span><span className="text-[#4A5A50] text-xs">{c.phone || c.city || ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-2">
            {stops.length === 0 ? <div className="text-xs text-[#4A5A50]">No stops yet — add customers above.</div> : (
              <ol className="space-y-1">
                {stops.map((s, i) => (
                  <li key={s.contact_id} className="flex items-center gap-2 border border-[#E4DFCF] rounded-lg px-2 py-1 text-sm">
                    <span className="text-xs w-5 text-[#4A5A50]">{i + 1}.</span>
                    <div className="flex-1">
                      <div>{s.contact_name}</div>
                      <div className="text-[10px] text-[#4A5A50]">{s.phone || s.address || ""}</div>
                    </div>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs text-[#4A5A50] disabled:opacity-30">↑</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === stops.length - 1} className="text-xs text-[#4A5A50] disabled:opacity-30">↓</button>
                    <button type="button" onClick={() => removeStop(i)}><X size={14} className="text-red-600"/></button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="route-save-btn">{saving ? <Loader2 size={14} className="animate-spin mr-1"/> : <Check size={14} className="mr-1"/>} Save route</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RouteDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/field-sales/route-plans/${id}`); setPlan(data); }
    catch { toast.error("Route not found"); nav("/field/routes"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const mark = async (stop_id, status) => {
    setBusy(stop_id);
    try {
      const { data } = await api.post(`/field-sales/route-plans/${id}/stops/${stop_id}/mark`, null, { params: { status } });
      setPlan(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  if (!plan) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;
  const done = (plan.stops || []).filter(s => s.status === "visited").length;

  return (
    <div className="space-y-6" data-testid="route-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <Header eyebrow={plan.plan_date ? format(new Date(plan.plan_date), "dd MMM yyyy · EEE") : ""} title={plan.title || `Route · ${plan.salesperson_name || ""}`}/>
      <div className="text-xs text-[#4A5A50]">{plan.salesperson_name} · {done}/{plan.stops?.length || 0} visited{plan.total_km_estimate != null ? ` · ~${plan.total_km_estimate} km` : ""}</div>
      <div className="space-y-2">
        {(plan.stops || []).map((s, i) => (
          <Card key={s.id} className={`border-[#E4DFCF] rounded-xl ${s.status === "visited" ? "bg-[#E1F0E6]/40" : s.status === "skipped" ? "bg-[#F1EDDF]/40" : ""}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[#0F5E39] text-white text-sm font-semibold">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.contact_name || "—"}</div>
                <div className="text-xs text-[#4A5A50] truncate">{[s.phone, s.address].filter(Boolean).join(" · ")}</div>
                {s.notes && <div className="text-xs text-[#4A5A50] mt-1">📝 {s.notes}</div>}
              </div>
              {s.contact_id && s.status !== "visited" && (
                <Link to={`/field/visits?contact_id=${s.contact_id}`} className="text-xs text-[#0F5E39] hover:underline hidden md:inline">Log visit</Link>
              )}
              <div className="flex gap-1">
                {s.status !== "visited" && <Button size="sm" className="bg-[#0F5E39] hover:bg-[#083F22] text-white h-8" onClick={() => mark(s.id, "visited")} disabled={busy === s.id} data-testid={`stop-visited-${s.id}`}>{busy === s.id ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}</Button>}
                {s.status === "pending" && <Button size="sm" variant="outline" className="h-8" onClick={() => mark(s.id, "skipped")}>Skip</Button>}
                {s.status !== "pending" && <Button size="sm" variant="outline" className="h-8" onClick={() => mark(s.id, "pending")}>Reset</Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// FOLLOW-UPS
// ============================================================
export function FollowUpsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = tab === "all" ? {} : { status: tab };
      const { data } = await api.get("/field-sales/follow-ups", { params });
      setRows(asList(data));
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const sortedRows = useMemo(() => [...rows].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority] ?? 1;
    const pb = PRIORITY_ORDER[b.priority] ?? 1;
    if (pa !== pb) return pa - pb;
    return new Date(a.due_date) - new Date(b.due_date);
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="follow-ups-page">
      <Header eyebrow="Field Sales" title="Follow-ups" actions={
        <Button className="rounded-full h-11 bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenNew(true)} data-testid="new-followup-btn"><Plus size={14} className="mr-1"/> New task</Button>
      }/>
      <div className="flex gap-1 border-b border-[#E4DFCF] overflow-x-auto">
        {[
          { k: "today", label: "Today" },
          { k: "overdue", label: "Overdue" },
          { k: "pending", label: "All pending" },
          { k: "done", label: "Done" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`fu-tab-${t.k}`}
            className={`px-3 py-2 text-sm border-b-2 ${tab === t.k ? "border-[#0F5E39] text-[#0F5E39] font-medium" : "border-transparent text-[#4A5A50]"}`}>{t.label}</button>
        ))}
      </div>
      {loading ? <div className="text-sm text-[#4A5A50]">Loading…</div> : sortedRows.length === 0 ? (
        <Card className="border-dashed border-[#E4DFCF] rounded-xl"><CardContent className="p-8 text-center text-sm text-[#4A5A50]"><ListTodo className="mx-auto mb-2 text-[#0F5E39]" size={32}/>Nothing here — you're all caught up.</CardContent></Card>
      ) : (
        <div className="space-y-2">{sortedRows.map(fu => <FollowUpRow key={fu.id} fu={fu} onChange={load}/>)}</div>
      )}
      <NewFollowUpDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} currentUser={user}/>
    </div>
  );
}

function FollowUpRow({ fu, onChange }) {
  const due = fu.due_date ? new Date(fu.due_date) : null;
  const overdue = due && fu.status === "pending" && isPast(due);
  const daysLeft = due ? differenceInDays(due, new Date()) : null;
  const priorityColor = fu.priority === "high" ? "text-red-700 bg-red-50" : fu.priority === "low" ? "text-[#4A5A50] bg-[#F1EDDF]" : "text-[#7A6329] bg-[#FCF7EA]";

  const markDone = async () => {
    try { await api.patch(`/field-sales/follow-ups/${fu.id}`, { status: "done" }); toast.success("Marked done"); onChange(); }
    catch { toast.error("Failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this follow-up?")) return;
    try { await api.delete(`/field-sales/follow-ups/${fu.id}`); toast.success("Deleted"); onChange(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card className={`border-[#E4DFCF] rounded-xl ${fu.status === "done" ? "opacity-60" : ""}`} data-testid={`fu-row-${fu.id}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <button onClick={markDone} disabled={fu.status !== "pending"}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${fu.status === "done" ? "bg-[#0F5E39] border-[#0F5E39]" : "border-[#4A5A50] hover:border-[#0F5E39]"}`}
          data-testid={`fu-check-${fu.id}`}>
          {fu.status === "done" && <Check size={12} className="text-white"/>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm ${fu.status === "done" ? "line-through" : "font-medium"}`}>{fu.action}</span>
            {fu.priority === "high" && <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${priorityColor}`}><Flame size={10} className="inline"/> high</span>}
            {overdue && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-50 text-red-700"><AlertTriangle size={10} className="inline"/> overdue</span>}
          </div>
          <div className="mt-1 text-xs text-[#4A5A50] flex items-center gap-3 flex-wrap">
            {due && <span className="inline-flex items-center gap-1"><Calendar size={10}/> {format(due, "dd MMM · h:mm a")}{daysLeft !== null && fu.status === "pending" && ` (${daysLeft === 0 ? "today" : daysLeft > 0 ? `in ${daysLeft}d` : `${-daysLeft}d ago`})`}</span>}
            {fu.contact_name && <span className="inline-flex items-center gap-1"><UserIcon size={10}/> {fu.contact_name}</span>}
            {fu.salesperson_name && <span className="inline-flex items-center gap-1 opacity-70">@ {fu.salesperson_name}</span>}
          </div>
        </div>
        <button onClick={remove} className="text-[#4A5A50] hover:text-red-600"><Trash2 size={14}/></button>
      </CardContent>
    </Card>
  );
}

function NewFollowUpDialog({ open, onClose, onSaved, currentUser }) {
  const [f, setF] = useState({ action: "", due_date: format(new Date(Date.now() + 86400 * 1000), "yyyy-MM-dd'T'HH:mm"), priority: "normal", contact_id: "", salesperson_id: "" });
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [selectedContactName, setSelectedContactName] = useState("");
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const canTeam = ["admin", "super-admin", "manager", "accounts"].includes(currentUser?.role);

  useEffect(() => {
    if (!open) { setF({ action: "", due_date: format(new Date(Date.now() + 86400 * 1000), "yyyy-MM-dd'T'HH:mm"), priority: "normal", contact_id: "", salesperson_id: "" }); setContactSearch(""); setContactResults([]); setSelectedContactName(""); }
    else if (canTeam) { (async () => { try { const { data } = await api.get("/field-sales/team/salespeople"); setTeam(asList(data)); } catch (err) { void err; } })(); }
  }, [open, canTeam]);

  useEffect(() => {
    if (!contactSearch || contactSearch.length < 2) { setContactResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/contacts", { params: { search: contactSearch, limit: 10 } }); if (!cancelled) setContactResults(asList(data)); } catch (err) { void err; }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [contactSearch]);

  const submit = async () => {
    if (!f.action.trim()) { toast.error("Action required"); return; }
    setSaving(true);
    try {
      await api.post("/field-sales/follow-ups", {
        action: f.action, due_date: new Date(f.due_date).toISOString(),
        priority: f.priority, contact_id: f.contact_id || null,
        salesperson_id: f.salesperson_id || null,
      });
      toast.success("Follow-up added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-followup-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New follow-up</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Action</Label><Textarea rows={2} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Call back for gold rate…" data-testid="fu-action"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due</Label><Input type="datetime-local" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="fu-due"/></div>
            <div><Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="fu-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {canTeam && (
            <div><Label>Assign to</Label>
              <Select value={f.salesperson_id || "me"} onValueChange={(v) => setF({ ...f, salesperson_id: v === "me" ? "" : v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">Me ({currentUser?.name})</SelectItem>
                  {team.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Contact (optional)</Label>
            {selectedContactName ? (
              <div className="flex items-center gap-2 border border-[#E4DFCF] rounded-lg px-3 py-2 text-sm">
                <span className="flex-1">{selectedContactName}</span>
                <button type="button" onClick={() => { setSelectedContactName(""); setF({ ...f, contact_id: "" }); }}><X size={12}/></button>
              </div>
            ) : (
              <>
                <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search customer…"/>
                {contactResults.length > 0 && (
                  <div className="mt-1 border border-[#E4DFCF] rounded-lg max-h-40 overflow-y-auto">
                    {contactResults.map(c => (
                      <button key={c.id} type="button" onClick={() => { setF({ ...f, contact_id: c.id }); setSelectedContactName(c.name); setContactSearch(""); setContactResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#F1EDDF] flex justify-between">
                        <span>{c.name}</span><span className="text-[#4A5A50] text-xs">{c.phone || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="fu-save-btn">{saving ? <Loader2 size={14} className="animate-spin mr-1"/> : <Check size={14} className="mr-1"/>} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// PERFORMANCE DASHBOARD
// ============================================================
export function PerformancePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/field-sales/performance/salespeople", { params: { days } }); setRows(data?.rows || []); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    visits: acc.visits + (r.visits || 0),
    exp: acc.exp + (r.expense_total || 0),
    contacts: acc.contacts + (r.unique_contacts || 0),
    overdue: acc.overdue + (r.follow_ups_overdue || 0),
  }), { visits: 0, exp: 0, contacts: 0, overdue: 0 }), [rows]);

  return (
    <div className="space-y-6" data-testid="performance-page">
      <Header eyebrow="Field Sales" title="Team performance" actions={
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      }/>
      {/* Aggregate cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Users} label="Visits" value={totals.visits}/>
        <MetricCard icon={UserIcon} label="Unique contacts" value={totals.contacts}/>
        <MetricCard icon={IndianRupee} label="Expenses" value={`₹ ${Math.round(totals.exp).toLocaleString("en-IN")}`}/>
        <MetricCard icon={AlertTriangle} label="Overdue follow-ups" value={totals.overdue} accent={totals.overdue > 0 ? "red" : ""}/>
      </div>
      {loading ? <div className="text-sm text-[#4A5A50]">Loading…</div> : rows.length === 0 ? (
        <Card className="border-dashed border-[#E4DFCF] rounded-xl"><CardContent className="p-8 text-center text-sm text-[#4A5A50]"><BarChart3 className="mx-auto mb-2 text-[#0F5E39]" size={32}/>No salespeople yet.</CardContent></Card>
      ) : (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#4A5A50] bg-[#F1EDDF]/50">
                <tr>
                  <th className="text-left p-3">Salesperson</th>
                  <th className="text-right p-3">Visits</th>
                  <th className="text-right p-3">Contacts</th>
                  <th className="text-right p-3">Avg time</th>
                  <th className="text-right p-3">Expenses</th>
                  <th className="text-right p-3">Follow-ups (Pending · Overdue · Done)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.salesperson_id} className={i > 0 ? "border-t border-[#E4DFCF]" : ""}>
                    <td className="p-3">
                      <div className="font-medium">{r.salesperson_name}</div>
                      <div className="text-xs text-[#4A5A50] capitalize">{r.role}</div>
                    </td>
                    <td className="p-3 text-right"><span className="inline-flex items-center gap-1"><TrendingUp size={12} className="text-[#0F5E39]"/> {r.visits}</span></td>
                    <td className="p-3 text-right">{r.unique_contacts}</td>
                    <td className="p-3 text-right">{r.avg_visit_minutes != null ? `${r.avg_visit_minutes} min` : "—"}</td>
                    <td className="p-3 text-right">₹ {Math.round(r.expense_total).toLocaleString("en-IN")}</td>
                    <td className="p-3 text-right">
                      <span className="text-[#7A6329]">{r.follow_ups_pending}</span>
                      <span className="mx-1 text-[#4A5A50]">·</span>
                      <span className={r.follow_ups_overdue > 0 ? "text-red-700 font-medium" : "text-[#4A5A50]"}>{r.follow_ups_overdue}</span>
                      <span className="mx-1 text-[#4A5A50]">·</span>
                      <span className="text-[#0F5E39]">{r.follow_ups_done}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  const color = accent === "red" ? "text-red-700" : "text-[#0F5E39]";
  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] inline-flex items-center gap-1"><Icon size={12}/> {label}</div>
      <div className={`mt-2 font-serif text-2xl ${color}`}>{value}</div>
    </CardContent></Card>
  );
}
